import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, ShieldCheck } from 'lucide-react';
import type { UserRoleToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useUserRolesList, useUserRoleMutations } from '~/hooks/use-user-roles';
import { APPOINTMENTS_MODULE, useHasSpecialModule } from '~/lib/auth/use-has-special-module';
import { tokenToForm, grantedPermissionCount, visibleRolePermissions, type UserRoleFormValues } from '~/lib/schemas/user-role';
import { UserRoleEditDialog } from './user-role-edit-dialog';

/** User roles CRUD surface. Grid summarizes the 12 permissions as a granted count; the editor has the full toggles. */
export function UserRolesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useUserRolesList();
	const { remove } = useUserRoleMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<UserRoleFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<UserRoleToken | null>(null);
	const permissions = visibleRolePermissions(useHasSpecialModule(APPOINTMENTS_MODULE));

	const columns = useMemo<SystemGridColumn<UserRoleToken>[]>(
		() => [
			{
				key: 'name',
				header: t('system.userRoles.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: r => r.nm,
				exportValue: r => r.nm,
				render: r => <span className="truncate text-sm font-medium text-foreground">{r.nm}</span>
			},
			{
				key: 'permissions',
				header: t('system.userRoles.columns.permissions'),
				width: '150px',
				sortValue: r => grantedPermissionCount(r, permissions),
				exportValue: r =>
					permissions
						.filter(p => r[p])
						.map(p => t(`system.userRoles.perms.${p}`))
						.join(', '),
				render: r => (
					<span className="rounded-pill bg-muted px-2 py-0.5 text-xs font-medium text-foreground-muted">
						{t('system.userRoles.permissionCount', { count: grantedPermissionCount(r, permissions), total: permissions.length })}
					</span>
				)
			},
			...permissions.map(
				(p): SystemGridColumn<UserRoleToken> => ({
					key: `perm:${p}`,
					header: t(`system.userRoles.perms.${p}`),
					width: '110px',
					align: 'center',
					defaultVisible: false,
					sortValue: r => (r[p] ? 1 : 0),
					exportValue: r => (r[p] ? t('common.yes') : t('common.no')),
					render: r => (r[p] ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
				})
			),
			{
				key: 'visible',
				header: t('system.userRoles.columns.visible'),
				width: '80px',
				align: 'center',
				sortValue: r => (r.iv ? 1 : 0),
				exportValue: r => (r.iv ? t('common.yes') : t('common.no')),
				render: r => (r.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('system.userRoles.columns.sort'),
				width: '70px',
				align: 'right',
				sortValue: r => r.si ?? 0,
				exportValue: r => r.si ?? 0,
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{r.si}</span>
			}
		],
		[t, permissions]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<UserRoleToken>
				gridId="user-roles"
				title={t('system.userRoles.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={r => r.id ?? r.nm}
				columns={columns}
				searchPredicate={(r, q) => (r.nm ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<ShieldCheck size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.userRoles.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={r => {
					setEditing(tokenToForm(r));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="user-roles"
			/>

			<UserRoleEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.userRoles.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.userRoles.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
